/**
 * Integrytas Group — Email Distribution System (EDS)
 * Page: EDS.EMAIL.LOG  —  Email Send Log (Read-Only)
 *
 * Sortable grid of eds_email_log rows with campaign / date / status filters.
 * Clicking a row expands a side panel showing all eds_email_event records
 * for that send, giving a full engagement timeline.
 */

import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { DateInput } from '../../components/common/DateInput';
import { useTranslation } from 'react-i18next';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { useActiveLocale } from '../../lib/use_active_locale';
import { apiDb } from '../../lib/api_client';
import type { AuthSession } from '../../types/auth';
import { SortableHeader } from '../../components/common/SortableHeader';
import { compareStrings, useSort } from '../../lib/use_sort';
import type { EdsEmailLogRow, EdsEmailEventRow, EdsLogSortKey } from '../../types/eds';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, GRID_TABLE_CLASS } from '../../components/personalization';
import { usePersonalization } from '../../lib/use_personalization';
import type { GridColumnDef } from '../../components/personalization';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { PageHeader } from '../../components/common/PageHeader';
import { ContentCard } from '../../components/common/ContentCard';
import { Search, RefreshCw } from 'lucide-react';
import { StatusBadge, type StatusVariant } from '../../components/common/StatusBadge';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { SkeletonRows } from '../../components/common/SkeletonRows';
import { EmptyState } from '../../components/common/EmptyState';

const GRID_COLS: GridColumnDef[] = [
  { key: 'to_email_address',     header: 'To Email',    defaultWidth: 200 },
  { key: 'campaign_name',        header: 'Campaign',    defaultWidth: 180 },
  { key: 'template_code',        header: 'Template',    defaultWidth: 160 },
  { key: 'delivery_status_code', header: 'Status',      defaultWidth: 110 },
  { key: 'queued_at',            header: 'Queued',      defaultWidth: 140 },
  { key: 'sent_at',              header: 'Sent',        defaultWidth: 140 },
];

// Delivery-status → badge variant (label comes localized from eds_delivery_status_tl).
const STATUS_VARIANT: Record<string, StatusVariant> = {
  queued:         'info',
  sent:           'info',
  delivered:      'success',
  soft_bounce:    'warning',
  hard_bounce:    'danger',
  spam_complaint: 'danger',
  failed:         'danger',
};

// Event-type → badge variant (label comes localized from eds_event_type_tl).
const EVENT_VARIANT: Record<string, StatusVariant> = {
  sent:         'info',
  delivered:    'success',
  opened:       'success',
  clicked:      'info',
  bounced:      'danger',
  complained:   'danger',
  unsubscribed: 'warning',
};

export interface EdsEmailLogPageProps { session: AuthSession; }

export function EdsEmailLogPage({ session }: EdsEmailLogPageProps) {
  return (
    <PersonalizationProvider pageCode="EDS.EMAIL.LOG" session={session}>
      <EdsEmailLogPageInner session={session} />
    </PersonalizationProvider>
  );
}

function EdsEmailLogPageInner({ session }: EdsEmailLogPageProps) {
  const { t } = useTranslation();
  // §8: resolve _tl data against the ACTIVE locale (follows a runtime switch), not
  // the stored preferred_locale (stale in memory until re-login).
  const activeLocale = useActiveLocale();
  const { isPersonalizing } = usePersonalization();
  const [rows, setRows]                 = useState<EdsEmailLogRow[]>([]);
  const [filter, setFilter]             = useState('');
  // §17.B5: per-column multi-value facets (delivery status, template).
  const [facets, setFacets]             = useState<{ status: string[]; template: string[] }>({ status: [], template: [] });
  const [dateFrom, setDateFrom]         = useState('');
  const [dateTo, setDateTo]             = useState('');
  const [selected, setSelected]         = useState<EdsEmailLogRow | null>(null);
  const [busy, setBusy]                 = useState(false);
  const [error, setError]               = useState<string | null>(null);
  // §17.B2: delivery_status_code / event_type_code → localized display_text.
  const [statusLabels, setStatusLabels] = useState<Record<string, string>>({});
  const [eventLabels, setEventLabels]   = useState<Record<string, string>>({});
  const sort = useSort<EdsLogSortKey>('eds.log.sort', { key: 'queued_at', dir: 'desc' });
  const detail = useExpandedRows<string>();

  // §17.B2: load delivery-status + event-type lookups (no hardcoded code lists).
  useEffect(() => {
    const locale = activeLocale;
    const buildMap = (rows: { code: string; language_id: string; display_text: string }[]) => {
      const byCode: Record<string, { language_id: string; display_text: string }[]> = {};
      for (const r of rows) (byCode[r.code] ??= []).push(r);
      const out: Record<string, string> = {};
      for (const [code, tls] of Object.entries(byCode)) {
        const exact = tls.find(t => t.language_id === locale);
        const fb = tls.find(t => t.language_id === 'EN_US') ?? tls[0];
        out[code] = exact?.display_text ?? fb?.display_text ?? code;
      }
      return out;
    };
    void apiDb.from('eds_delivery_status_tl').select('delivery_status_code, language_id, display_text')
      .then(({ data }) => setStatusLabels(buildMap((data ?? []).map((r: { delivery_status_code: string; language_id: string; display_text: string }) => ({ code: r.delivery_status_code, language_id: r.language_id, display_text: r.display_text })))));
    void apiDb.from('eds_event_type_tl').select('event_type_code, language_id, display_text')
      .then(({ data }) => setEventLabels(buildMap((data ?? []).map((r: { event_type_code: string; language_id: string; display_text: string }) => ({ code: r.event_type_code, language_id: r.language_id, display_text: r.display_text })))));
  }, [activeLocale]);

  const reload = useCallback(async () => {
    setBusy(true); setError(null);
    try {
      let q = apiDb
        .from('eds_email_log')
        .select(`
          log_id, campaign_id, template_id, recipient_id, to_email_address,
          language_id, delivery_status_code, provider_message_id,
          queued_at, sent_at, delivered_at, failed_reason, created_at,
          eds_campaign!left(eds_campaign_tl!left(campaign_name, language_id)),
          eds_email_template!left(internal_code, from_name_default)
        `)
        .order('queued_at', { ascending: false })
        .limit(500);

      if (dateFrom) q = q.gte('queued_at', new Date(dateFrom).toISOString());
      if (dateTo)   q = q.lte('queued_at', new Date(dateTo + 'T23:59:59').toISOString());

      const { data, error: e } = await q;
      if (e) throw e;
      type CampaignTl = TlRowText & { campaign_name?: string };
      type Row = EdsEmailLogRow & {
        eds_campaign?: { eds_campaign_tl?: CampaignTl[] } | null;
        eds_email_template?: { internal_code: string; from_name_default: string } | null;
      };
      const flat = ((data ?? []) as Row[]).map(r => {
        const ctls = r.eds_campaign?.eds_campaign_tl ?? [];
        const camTl = ctls.find(t => t.language_id === activeLocale)   // §8: active locale first
                      ?? ctls.find(t => t.language_id === 'EN_US') ?? ctls[0];
        return {
          ...r,
          campaign_name: camTl?.campaign_name ?? (r.campaign_id ? r.campaign_id.slice(0, 8) + '…' : '(transactional)'),
          template_internal_code: r.eds_email_template?.internal_code ?? r.template_id,
          // §17.B4: grid must show the template's resolved name, never its raw internal_code.
          template_display_name: r.eds_email_template?.from_name_default ?? r.eds_email_template?.internal_code ?? r.template_id,
        } as EdsEmailLogRow;
      });
      setRows(flat);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
    // activeLocale: the _tl label picked below must follow the active language.
  }, [dateFrom, dateTo, activeLocale]);

  useEffect(() => { void reload(); }, [reload]);

  // §17.B5: distinct facet options derived from loaded rows + the status lookup.
  const facetOptions = useMemo(() => {
    const uniq = (vals: string[]) => [...new Set(vals.filter(Boolean))].sort();
    return {
      status:   uniq(rows.map(r => r.delivery_status_code)).map(v => ({ value: v, label: statusLabels[v] ?? v })),
      template: uniq(rows.map(r => r.template_internal_code ?? '')).map(v => ({
        value: v,
        label: rows.find(r => r.template_internal_code === v)?.template_display_name ?? v,
      })),
    };
  }, [rows, statusLabels]);

  const facetsActive = facets.status.length + facets.template.length > 0;
  const anyFilterActive = facetsActive || filter.trim().length > 0 || !!dateFrom || !!dateTo;
  const clearAll = () => {
    setFilter('');
    setFacets({ status: [], template: [] });
    setDateFrom('');
    setDateTo('');
  };

  const filtered = useMemo(() => {
    const base = rows.filter(r => {
      const matchText = !filter.trim() ||
        r.to_email_address.toLowerCase().includes(filter.toLowerCase()) ||
        (r.campaign_name ?? '').toLowerCase().includes(filter.toLowerCase()) ||
        (r.template_internal_code ?? '').toLowerCase().includes(filter.toLowerCase());
      // OR within a facet, AND across facets.
      const matchFacets =
        (facets.status.length   === 0 || facets.status.includes(r.delivery_status_code)) &&
        (facets.template.length === 0 || facets.template.includes(r.template_internal_code ?? ''));
      return matchText && matchFacets;
    });
    return sort.sorted(base, (a, b, key) => compareStrings(String((a as unknown as Record<string, unknown>)[key] ?? ''), String((b as unknown as Record<string, unknown>)[key] ?? '')));
  }, [rows, filter, facets, sort]);

  // §17.B13 related-children: the send's parent template (resolved name; raw code rides along as title).
  const buildRelatedGroups = (id: string): RelatedChildGroup[] => {
    const r = rows.find(x => x.log_id === id);
    const name = r?.template_display_name ?? r?.template_internal_code;
    return [
      { key: 'template', title: t('eds.email_log.related.template', 'Template'),
        items: r && name ? [{ id: 'template', label: name, title: r.template_internal_code }] : [] },
    ];
  };
  const hasRelated = (id: string) => buildRelatedGroups(id).some(g => g.items.length > 0);

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('eds.email_log.title', 'Email Send Log')}
          description={t('eds.email_log.list.description', 'Review every sent email, its delivery status and the full engagement timeline.')}
          actions={
            <button type="button" onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5">
              <RefreshCw className="h-3.5 w-3.5" /> {t('common.refresh')}
            </button>
          }
        />

        {error && (
          <ErrorBanner
            message={t('eds.email_log.load_failed', "Couldn't load the email log.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        )}

        <ContentCard className="flex min-h-0 flex-1 flex-col">
          {/* Toolbar */}
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input type="text" value={filter} onChange={e => setFilter(e.target.value)}
                     placeholder={t('eds.email_log.search_placeholder', 'Search email / campaign…')}
                     className="h-8 w-60 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none" />
            </div>
            <FacetFilter label={t('eds.email_log.columns.status', 'Status')}     options={facetOptions.status}   selected={facets.status}   onChange={v => setFacets(f => ({ ...f, status: v }))} />
            <FacetFilter label={t('eds.email_log.columns.template', 'Template')}  options={facetOptions.template} selected={facets.template} onChange={v => setFacets(f => ({ ...f, template: v }))} />
            <div className="flex items-center gap-1 text-csm-xs text-csm-muted">
              {t('eds.email_log.date_from')} <DateInput value={dateFrom} onChange={setDateFrom}
                          className="h-8 rounded-csm-control border border-csm-border px-1 text-csm-sm focus:outline-none" />
              {t('eds.email_log.date_to')}   <DateInput value={dateTo}   onChange={setDateTo}
                          className="h-8 rounded-csm-control border border-csm-border px-1 text-csm-sm focus:outline-none" />
            </div>
            <ClearFiltersButton active={anyFilterActive} onClear={clearAll} label={t('common.clear_filters', 'Clear filters')} />
            <div className="ml-auto flex items-center gap-2">
              <span className="text-csm-xs text-csm-muted">{filtered.length} row{filtered.length !== 1 ? 's' : ''}</span>
              {busy && <span className="text-csm-xs text-csm-muted">{t('common.loading')}</span>}
              <PersonalizationToggle />
            </div>
          </div>
          {isPersonalizing && (
            <div className="shrink-0 border-b border-primary/20 bg-primary/5 px-3 py-1 text-csm-xs text-primary">
              Personalization mode — configure columns, then click the icon to save and exit.
            </div>
          )}

          <div className="flex min-h-0 flex-1">
        {/* Grid */}
        <div className="flex-1 overflow-auto">
          {busy && rows.length === 0 ? (
            <SkeletonRows rows={8} />
          ) : (
          <PersonalizedGrid componentKey="grid" columns={GRID_COLS}>
            {(visibleCols, widths) => (
              <table className={GRID_TABLE_CLASS}>
                <thead className="sticky top-0 z-10 bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                  <tr>
                    <th className="w-7 border-b border-csm-border px-1 py-1.5" title={t('common.related.header', 'Related records')} />
                    {visibleCols.map(col => {
                      if (col.key === 'template_code') {
                        return (
                          <th key={col.key} className="border-b border-csm-border px-3 py-1.5 text-left"
                              style={widths[col.key] ? { width: widths[col.key], minWidth: widths[col.key] } : undefined}>
                            {col.header}
                          </th>
                        );
                      }
                      return (
                        <SortableHeader<EdsLogSortKey> key={col.key}
                          sortKey={col.key as EdsLogSortKey} label={col.header}
                          currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort}
                          style={widths[col.key] ? { width: widths[col.key], minWidth: widths[col.key] } : undefined} />
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(r => (
                    <Fragment key={r.log_id}>
                    <tr onClick={() => setSelected(r === selected ? null : r)}
                        className={`cursor-pointer ${selected?.log_id === r.log_id ? 'bg-primary-soft' : 'hover:bg-csm-surface-alt'}`}>
                      <td className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle" onClick={e => e.stopPropagation()}>
                        <RelatedExpandButton
                          open={detail.isOpen(r.log_id)}
                          hasChildren={hasRelated(r.log_id)}
                          onToggle={() => detail.toggle(r.log_id)}
                          expandLabel={t('common.related.show', 'Show related records')}
                          collapseLabel={t('common.related.hide', 'Hide related')}
                          tooltip={t('common.related.tooltip', 'Show related records')}
                        />
                      </td>
                      {visibleCols.map(col => (
                        <td key={col.key} className="border-b border-csm-border-soft px-3 py-1.5"
                            style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                          {col.key === 'to_email_address'     && <span className="font-mono text-csm-sm">{r.to_email_address}</span>}
                          {col.key === 'campaign_name'        && <span className="text-csm-ink-soft">{r.campaign_name}</span>}
                          {col.key === 'template_code'        && <span className="text-csm-xs text-csm-muted">{r.template_display_name ?? r.template_internal_code}</span>}
                          {col.key === 'delivery_status_code' && (
                            <StatusBadge
                              variant={STATUS_VARIANT[r.delivery_status_code] ?? 'neutral'}
                              label={statusLabels[r.delivery_status_code] ?? r.delivery_status_code}
                            />
                          )}
                          {col.key === 'queued_at' && <span className="text-csm-sm text-csm-ink-soft">{new Date(r.queued_at).toLocaleString()}</span>}
                          {col.key === 'sent_at'   && (
                            <span className="text-csm-sm text-csm-ink-soft">
                              {r.sent_at ? new Date(r.sent_at).toLocaleString() : <span className="italic text-csm-muted">—</span>}
                            </span>
                          )}
                        </td>
                      ))}
                    </tr>
                    {detail.isOpen(r.log_id) && (
                      <tr>
                        <td colSpan={visibleCols.length + 1} className="border-b border-csm-border-soft bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                          <RelatedChildrenPanel groups={buildRelatedGroups(r.log_id)} session={session} />
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  ))}
                  {filtered.length === 0 && !busy && (
                    <tr><td colSpan={visibleCols.length + 1} className="px-3 py-6 text-center">
                      <EmptyState
                        variant={anyFilterActive ? 'no-match' : 'no-data'}
                        title={t('eds.email_log.no_results')}
                      />
                    </td></tr>
                  )}
                </tbody>
              </table>
            )}
          </PersonalizedGrid>
          )}
        </div>

        {/* Events side panel */}
        {selected && (
          <div className="w-80 shrink-0 border-l border-csm-border bg-white overflow-y-auto">
            <EventsPanel log={selected} eventLabels={eventLabels} onClose={() => setSelected(null)} />
          </div>
        )}
          </div>
        </ContentCard>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Events panel
// ---------------------------------------------------------------------------

function EventsPanel({ log, eventLabels, onClose }: { log: EdsEmailLogRow; eventLabels: Record<string, string>; onClose: () => void }) {
  const { t } = useTranslation();
  const [events, setEvents] = useState<EdsEmailEventRow[]>([]);
  const [busy, setBusy]     = useState(false);

  useEffect(() => {
    setBusy(true);
    void apiDb.from('eds_email_event').select('*').eq('log_id', log.log_id).order('occurred_at')
      .then(({ data }) => { setEvents((data ?? []) as EdsEmailEventRow[]); setBusy(false); });
  }, [log.log_id]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex h-9 shrink-0 items-center justify-between border-b border-csm-border bg-csm-surface-alt px-3">
        <span className="text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{t('eds.email_log.events_panel.title')}</span>
        <button onClick={onClose} className="text-csm-muted hover:text-csm-ink text-csm-sm">✕</button>
      </div>
      <div className="p-3 border-b border-csm-border-soft">
        <div className="font-mono text-csm-xs text-csm-muted truncate">{log.to_email_address}</div>
        <div className="mt-0.5 font-mono text-csm-2xs text-csm-muted">{log.log_id}</div>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {busy && <div className="text-csm-xs text-csm-muted italic">{t('common.loading')}</div>}
        {!busy && events.length === 0 && <div className="text-csm-xs italic text-csm-muted">{t('eds.email_log.events_panel.empty')}</div>}
        {events.map(ev => (
          <div key={ev.event_id} className="rounded border border-csm-border bg-csm-surface-alt p-2">
            <div className="flex items-center gap-2">
              <StatusBadge
                variant={EVENT_VARIANT[ev.event_type_code] ?? 'neutral'}
                label={eventLabels[ev.event_type_code] ?? ev.event_type_code}
              />
              <span className="text-csm-2xs text-csm-muted">{new Date(ev.occurred_at).toLocaleString()}</span>
            </div>
            {ev.click_url && (
              <div className="mt-1 font-mono text-csm-2xs text-csm-ink-soft truncate" title={ev.click_url}>{ev.click_url}</div>
            )}
            {ev.bounce_detail && (
              <div className="mt-1 text-csm-2xs text-csm-danger">{ev.bounce_detail}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
